import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsCarBooking';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './TipoVehiculoModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';

const PAGE_SIZE = 10;
const nombreTabla = "Catálogo Tipo Vehículo";
const endpoint = ENDPOINTS.catTipoVehiculo;
const endpointById = ENDPOINTS.catTipoVehiculoById;
const modelo = Models.tipoVehiculo;

const maps = {
    mapCreate: (f) => ({
        clave: f.clave,
        descripcion: f.descripcion,
        fechaCreacion: new Date().toISOString().split('T')[0],
    }),
    mapUpdate: (id, f) => ({
        idTipoVehiculo: id,
        clave: f.clave,
        descripcion: f.descripcion,
        fechaCreacion: new Date().toISOString().split('T')[0],
    }),
};

export default function Catalog() {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [query, setQuery] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('new');
    const [selectedId, setSelectedId] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
    const [editData, setEditData] = useState(null);
    const [editingRowId, setEditingRowId] = useState(null);
    const [page, setPage] = useState(1);

    const editorRef = useRef(null);

    useEffect(() => {
        if (modalOpen && editorRef.current) {
            editorRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [modalOpen, selectedId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(endpoint);
            setRows((res.data || []).map((x) => modelo(x)));
        } catch (e) {
            console.error(e);
            alert('Error al cargar información');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => {
            const clave = (r.clave || '').toLowerCase();
            const desc = (r.descripcion || '').toLowerCase();
            return clave.includes(q) || desc.includes(q);
        });
    }, [rows, query]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

    const paged = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    const handleNew = () => {
        setSelectedId(null);
        setEditData(null);
        setModalMode('new');
        setModalOpen(true);
        setEditingRowId(null);
    };

    const handleEdit = (id) => {
        const r = rows.find(x => x.idTipoVehiculo === id);
        if (!r) {
            alert('No se encontró la fila en memoria.');
            return;
        }
        setSelectedId(id);
        setEditData(r);
        setModalMode('edit');
        setModalOpen(true);
        setEditingRowId(id);
    };

    const askDelete = (id, name) => {
        setDeleteTarget({ id, name });
        setDeleteOpen(true);
    };

    const doDelete = async () => {
        try {
            setLoading(true);
            await axios.delete(endpointById(deleteTarget.id));
            setDeleteOpen(false);
            setEditingRowId(null);
            await loadData();
        } catch (e) {
            console.error(e);
            alert('Error al eliminar');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (payload, mode) => {
        try {
            setLoading(true);
            if (mode === 'new') {
                const res = await axios.post(endpoint, maps.mapCreate(payload));
                const newId = res?.data?.idTipoVehiculo ?? (typeof res?.data === 'number' ? res.data : null);
                if (newId) setEditingRowId(newId);
            } else {
                await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
                setEditingRowId(selectedId);
            }
            setModalOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            alert('Error al guardar');
        } finally {
            setLoading(false);
        }
    };

    const PageBtn = ({ label, onClick, active, disabled }) => (
        <button
            type="button"
            className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
            onClick={onClick}
            disabled={disabled}
        >
            {label}
        </button>
    );

    const pageNumbers = (() => {
        const maxToShow = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxToShow - 1);
        start = Math.max(1, end - maxToShow + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    })();

    const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

    return (
        <div className="panel-sigma-main">
            <div className="roles-shell">
                <div className="roles-topbar">
                    <h2 className="roles-title">{nombreTabla}</h2>

                    <input
                        className="roles-search"
                        placeholder="Buscar por clave o descripción…"
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                    />
                    <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
                </div>

                <div className="roles-table-wrap">
                    <div className="roles-table-scroller">
                        <table className="roles-table">
                            <thead>
                                <tr>
                                    <th>Clave</th>
                                    <th>Descripción</th>
                                    <th style={{ textAlign: 'center' }}>Editar</th>
                                    <th style={{ textAlign: 'center' }}>Borrar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.map((r) => (
                                    <tr key={r.idTipoVehiculo}>
                                        <td><strong>{normalizeValue(r.clave)}</strong></td>
                                        <td>{normalizeValue(r.descripcion)}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                type="button"
                                                className="rk-iconbtn rk-iconbtn--edit"
                                                onClick={() => handleEdit(r.idTipoVehiculo)}
                                                aria-label="Editar"
                                                title="Editar"
                                            >
                                                <img src={sigmaEdit} alt="" className="rk-ico" />
                                            </button>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                type="button"
                                                className="rk-iconbtn rk-iconbtn--del"
                                                onClick={() => {
                                                    setEditingRowId(null);
                                                    const name = r.descripcion || r.clave || 'Tipo Vehículo';
                                                    askDelete(r.idTipoVehiculo, name);
                                                }}
                                                aria-label="Borrar"
                                                title="Borrar"
                                            >
                                                <img src={sigmaDelete} alt="" className="rk-ico" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {!loading && paged.length === 0 && (
                                    <tr><td colSpan={4}>Sin resultados.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="roles-pagination">
                    <div className="roles-pagination-left">
                        {filtered.length > 0
                            ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
                            : <span>0 resultados</span>}
                    </div>
                    <div className="roles-pagination-right">
                        <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
                        {pageNumbers.map((n) => (
                            <PageBtn
                                key={n}
                                label={n}
                                onClick={() => setPage(n)}
                                active={n === page}
                            />
                        ))}
                        <PageBtn
                            label="›"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                        />
                    </div>
                </div>

                {modalOpen && (
                    <div className="roles-editor-slot" ref={editorRef}>
                        <Modal
                            open={modalOpen}
                            mode={modalMode}
                            data={editData}
                            inline
                            onClose={() => setModalOpen(false)}
                            onSubmit={handleSubmit}
                        />
                    </div>
                )}
            </div>

            <ConfirmDeleteDialog
                open={deleteOpen}
                roleName={deleteTarget.name}
                onClose={() => setDeleteOpen(false)}
                onConfirm={doDelete}
            />
        </div>
    );
}
